import { Logo } from '@/components/logo'
import { OnboardingLayout } from '@/components/onboarding-layout'
import { Button, EmailInput, Group, Field, Notice } from '@tempots/beatui'
import { attr, html, OneOfValue, prop, Use, on } from '@tempots/dom'
import { HTMLTitle, Location } from '@tempots/ui'
import { ApiError, Auth } from '@/services/auth-service'
import { RequireAnon } from '@/components/require-anon'
import { Track } from '@/services/track'

export function ForgotPasswordPage() {
  return RequireAnon({}, () =>
    Use(Location, location => {
      const email = prop('')
      const submitting = prop(false)
      const feedbackState = prop<'idle' | 'success' | 'error'>('idle')
      const feedbackMessage = prop<string>('')

      const resetFeedback = () => {
        feedbackState.set('idle')
        feedbackMessage.set('')
      }

      const handleSubmit = async () => {
        Track.click({
          section: 'main',
          element_type: 'button',
          click_action: 'submit',
          element_id: 'forgot_password',
        })
        const value = email.value.trim()

        if (!value) {
          feedbackState.set('error')
          feedbackMessage.set(
            'Please enter the email associated with your account.'
          )
          Track.clientError({
            error_code: 'FORGOT_PASSWORD_EMPTY_EMAIL',
            error_message: 'Email is required',
          })
          return
        }

        try {
          submitting.set(true)
          const message = await Auth.requestPasswordReset(value)
          Track.auth({
            action: 'password_reset',
            message: 'Password reset requested',
          })
          feedbackState.set('success')
          feedbackMessage.set(message)
        } catch (error) {
          Track.apiError({
            error_code: 'PASSWORD_RESET_REQUEST_FAILED',
            error_message:
              error instanceof ApiError ? error.message : 'Unknown error',
          })
          feedbackState.set('error')
          if (error instanceof ApiError) {
            feedbackMessage.set(
              error.message ||
                'We could not process that request right now. Please try again later.'
            )
          } else {
            feedbackMessage.set(
              'We could not process that request right now. Please try again later.'
            )
          }
        } finally {
          submitting.set(false)
        }
      }

      return OnboardingLayout(
        {},
        HTMLTitle('Forgot Password - ResumeHeaders'),
        html.div(
          attr.class('w-full md:max-w-md'),
          html.div(
            attr.class('flex justify-center mb-6 md:mb-8'),
            Logo({ size: 'main' })
          ),
          html.h2(
            attr.class('text-lg md:text-xl font-bold text-center mb-3 md:mb-4'),
            'Forgot your password?'
          ),
          html.p(
            attr.class('text-center text-gray-600 mb-6 text-sm md:text-base'),
            'Enter your email address and we will send you a link to reset your password.'
          ),
          html.form(
            attr.class('flex flex-col gap-4'),
            on.submit(event => {
              event.preventDefault()
              void handleSubmit()
            }),
            Field({
              label: 'Email address',
              content: EmailInput({
                value: email,
                placeholder: 'you@example.com',
                id: 'email-input',
                onInput: v => {
                  email.set(v)
                  resetFeedback()
                },
              }),
            }),
            html.div(
              attr.id('submit-button'),
              Button(
                {
                  type: 'submit',
                  variant: 'filled',
                  color: 'primary',
                  loading: submitting,
                  disabled: submitting,
                },
                Group(
                  attr.class('items-center gap-2 justify-center'),
                  html.span('Send reset link')
                )
              )
            ),
            OneOfValue(feedbackState, {
              success: () =>
                html.div(
                  attr.id('success-message'),
                  Notice(
                    {
                      variant: 'success',
                      tone: 'prominent',
                      title: 'Check your inbox',
                    },
                    feedbackMessage.map(message => message ?? '')
                  )
                ),
              error: () =>
                html.div(
                  attr.id('error-message'),
                  Notice(
                    {
                      variant: 'danger',
                      tone: 'prominent',
                      title: 'Something went wrong',
                    },
                    feedbackMessage.map(message => message ?? '')
                  )
                ),
              idle: () => html.span(),
            })
          ),
          html.div(
            attr.class(
              'mt-8 flex flex-col gap-2 text-center text-sm text-gray-600'
            ),
            html.span(
              'Return to ',
              html.button(
                attr.type('button'),
                attr.class('text-teal-600 hover:underline font-medium'),
                on.click(() => {
                  Track.click({
                    section: 'main',
                    element_type: 'button',
                    click_action: 'navigate',
                    element_id: 'signin',
                  })
                  location.navigate('/signin')
                }),
                'Sign in'
              ),
              '.'
            ),
            html.span(
              'New here? ',
              html.button(
                attr.type('button'),
                attr.class('text-teal-600 hover:underline font-medium'),
                on.click(() => {
                  Track.click({
                    section: 'main',
                    element_type: 'button',
                    click_action: 'navigate',
                    element_id: 'signup',
                  })
                  location.navigate('/signup')
                }),
                'Create an account'
              ),
              '.'
            )
          )
        )
      )
    })
  )
}
